/**
 * Persists the mailbox's account-selector choice per-device so it survives
 * navigating away (to the Accounts view and back) and closing/reopening the
 * arche later. Like the list- and swipe-prefs hooks, this is per-device UI
 * state — which inbox you were last looking at — rather than account data, so
 * it lives in localStorage under the same `fias.email.*` namespace.
 *
 * The stored value is the raw selector string: a concrete `accountId`, the
 * unified "all accounts" sentinel (`'__all__'`), or `null` when nothing was
 * chosen (the default — the primary account). Validation of a stored value
 * against the live account list stays in the mailbox, which owns that data: a
 * stored account can outlive the account it named, and the unified option only
 * exists with ≥2 connected accounts.
 */

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'fias.email.activeAccount';

/** Read the persisted selector value, or `null` when absent/unavailable. */
export function loadActiveEmailAccount(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? null;
  } catch {
    // ignore unavailable storage
    return null;
  }
}

/**
 * A `useState`-shaped hook whose value is mirrored to localStorage. There is
 * only ever one mounted mailbox, so unlike `useEmailListPrefs` this needs no
 * same-tab sync event — a fresh mount simply reads the last stored value.
 */
export function useEmailActiveAccount(): [string | null, (next: string | null) => void] {
  const [value, setValue] = useState<string | null>(() => loadActiveEmailAccount());

  useEffect(() => {
    try {
      if (value === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignore unavailable storage
    }
  }, [value]);

  return [value, setValue];
}
